import React, { useCallback, useState } from 'react';

import { Icon, RadioButton } from '@rbilabs/components-library';
import dlv from 'dlv';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { IBaseProps } from '@rbi-ctg/frontend';
import IconDeleteCredit from 'components/icons/delete-credit';
import LoadingAnimation from 'components/loading-animation';
import { useCardDeletionConfirmation } from 'components/th-scan-and-pay/hooks/use-card-deletion-confirmation';
import { useScanAndPay } from 'components/th-scan-and-pay/hooks/use-scan-and-pay';
import { Tooltip } from 'components/tooltip';
import useDialogModal from 'hooks/use-dialog-modal';
import { useAuthContext } from 'state/auth';
import { CardTypes, ICredit, IPaymentMethod } from 'state/payment/types';
import { mapPaymentMethodCardTypes } from 'utils/payment/map-payment-method-card-type';
import { relativeRoutes } from 'utils/routing';
import { toast } from 'utils/toast';

import CashMethod from './cash-method';
import { cardIconRenderer } from './credit-card-icon';
import PayPalMethod from './paypal-method';
import PrepaidMethodBalance from './prepaid-method-balance';
import {
  CreditCardAlias,
  DefaultIndicatorContainer,
  EndsInWrapper,
  MethodContainer,
  MethodType,
  MethodTypeDescription,
  MethodTypeWrapper,
  RemoveMethod,
  RemoveMethodWrapper,
  StyledIconCash,
  StyledIconPaypal,
  StyledIconPrepaidCard,
} from './styled';
import { UnverifiedMethod } from './unverified-method';

export interface IPaymentMethodOptionProps extends IBaseProps {
  method: IPaymentMethod;
  selected?: boolean;
  onClick?: (fdAccountId: string) => void;
  showDefault?: boolean;
  removable?: boolean;
  readonly?: boolean;
  deletePaymentMethod?: (fdAccountId: string) => Promise<void>;
  hideDeleteIcon?: boolean;
  disableHover?: boolean;
  disableMethod?: boolean;
  hideBorder?: boolean;
  ariaLabelledby?: string;
  revaultRequired?: boolean;
  fromCheckout?: boolean;
}

function PaymentMethodOption({
  className,
  method,
  selected,
  onClick,
  showDefault,
  removable,
  deletePaymentMethod,
  disableHover = false,
  disableMethod = false,
  hideDeleteIcon = false,
  hideBorder = false,
  ariaLabelledby = 'payment-method-loads-with payment-method-label payment-method-card payment-method-alias payment-method-expirationDate',
  revaultRequired,
  fromCheckout = false,
}: IPaymentMethodOptionProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const { scanAndPayEnabledForUser, updateScanAndPayStatus } = useScanAndPay();

  const accountIdentifier = method.accountIdentifier ?? method.fdAccountId ?? '';
  const isSelectedScanAndPayPaymentMethod =
    accountIdentifier === user?.details.defaultScanAndPayAccountIdentifier &&
    scanAndPayEnabledForUser;

  const onDeleteMethod = useCallback(() => {
    if (deletePaymentMethod) {
      setIsDeleting(true);
      deletePaymentMethod(accountIdentifier).finally(() => {
        setIsDeleting(false);
        if (isSelectedScanAndPayPaymentMethod) {
          toast.success(formatMessage({ id: 'scanAndPayHasBeenDisabled' }));
        }
      });
    }
  }, [deletePaymentMethod, accountIdentifier, isSelectedScanAndPayPaymentMethod, formatMessage]);

  const onClickMethod = useCallback(() => {
    if (disableMethod) {
      return;
    }
    if (typeof onClick === 'function') {
      onClick(accountIdentifier);
    }
  }, [disableMethod, onClick, accountIdentifier]);

  const deleteScanAndPayPaymentMethod = useCallback(async () => {
    const updateResult = await updateScanAndPayStatus(false);
    const scanAndPayValue = updateResult.data?.setLoyaltyScanAndPay?.scanAndPay;
    if (!scanAndPayValue) {
      onDeleteMethod();
    } else {
      //what should we show here? I'm proposing this toast toast, but I didn't see it designed anywhere
    }
  }, [onDeleteMethod, updateScanAndPayStatus]);

  const [DeletionConfirmationDialog, openDeletionConfirmationDialog] = useDialogModal({
    onConfirm: onDeleteMethod,
    showCancel: true,
    modalAppearanceEventMessage: 'Confirmation: Delete payment method',
  });

  const [
    ScanAndPayCardDeletionDialog,
    openScanAndPayCardDeletionConfirmationDialog,
  ] = useCardDeletionConfirmation({
    onConfirmDelete: deleteScanAndPayPaymentMethod,
    onDismiss: () => {},
  });

  const openPaymentDeletionConfirmation = useCallback(() => {
    if (isSelectedScanAndPayPaymentMethod) {
      return openScanAndPayCardDeletionConfirmationDialog();
    }

    return openDeletionConfirmationDialog();
  }, [
    isSelectedScanAndPayPaymentMethod,
    openDeletionConfirmationDialog,
    openScanAndPayCardDeletionConfirmationDialog,
  ]);

  const onClickRevault = useCallback(
    e => {
      e.stopPropagation();
      navigate(relativeRoutes.addCard, { state: { accountToDelete: accountIdentifier } });
    },
    [accountIdentifier, navigate]
  );

  const expiration = (credit: ICredit) => {
    const { expiryYear: expYr, expiryMonth: expMon } = credit || {};
    if (!expYr || !expMon) {
      return;
    }
    const expirationDate = new Date(2000 + parseInt(expYr, 10), parseInt(expMon, 10));
    const currentDate = new Date();
    const isValid = expirationDate.getTime() - currentDate.getTime();
    return (
      <span id="payment-method-expirationDate">
        &nbsp;
        {`${
          isValid ? formatMessage({ id: 'expire' }) : formatMessage({ id: 'expired' })
        } ${expMon}/${expYr}`}
      </span>
    );
  };

  const RenderMethodType = () => {
    if (method.credit) {
      const creditCardAlias = (
        <CreditCardAlias>
          <EndsInWrapper>{formatMessage({ id: 'endsIn' }).toLowerCase()}</EndsInWrapper>
          <div>{method.credit.alias}</div>
        </CreditCardAlias>
      );
      let isCreditCard;
      let alias;
      let cardType: CardTypes | undefined;
      switch (method.credit.cardType) {
        case CardTypes.APPLE_PAY:
          isCreditCard = false;
          alias = formatMessage({ id: 'applePay' });
          break;
        case CardTypes.GOOGLE_PAY:
          isCreditCard = false;
          alias = formatMessage({ id: 'googlePay' });
          break;
        default:
          cardType = mapPaymentMethodCardTypes(method.credit.cardType);
          isCreditCard = true;
          alias = creditCardAlias;
      }
      return (
        <>
          {isCreditCard && <span id="payment-method-card">{cardType || 'CARD'}</span>}
          <MethodType id="payment-method-alias">{alias}</MethodType>
          {isCreditCard && expiration(method.credit)}
        </>
      );
    }

    if (method.prepaid) {
      return <PrepaidMethodBalance method={method} />;
    }

    if (method.cash) {
      return <CashMethod />;
    }

    if (method.paypal) {
      return <PayPalMethod />;
    }

    return null;
  };

  return isDeleting ? (
    <MethodContainer disableHover={disableHover} hideBorder={hideBorder}>
      <LoadingAnimation fillColor="#000000" />
    </MethodContainer>
  ) : (
    <MethodContainer
      disableHover={disableHover}
      disableMethod={disableMethod}
      hideBorder={hideBorder}
      className={className}
      role="button"
      aria-labelledby={ariaLabelledby}
      tabIndex={0}
      data-testid={`payment-method-${method.accountIdentifier ?? method.fdAccountId ?? ''}`}
    >
      <MethodTypeWrapper
        data-private
        onClick={onClickMethod}
        $isClickable={!!onClick}
        disableMethod={disableMethod}
        fromCheckout={fromCheckout}
        data-testid={`method-type-wrapper-${method.accountIdentifier ?? method.fdAccountId ?? ''}`}
      >
        {method.credit && cardIconRenderer(method.credit.cardType)}
        <div>
          <MethodTypeDescription>
            {method.prepaid && <StyledIconPrepaidCard />}
            {method.cash && (
              <StyledIconCash>
                <Icon icon="paymentCash" color="icon-default" width="24px" aria-hidden />
              </StyledIconCash>
            )}
            {method.paypal && (
              <StyledIconPaypal>
                <img
                  src="https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_37x23.jpg"
                  alt=""
                  width="32px"
                />
              </StyledIconPaypal>
            )}
            <RenderMethodType />
            {disableMethod && <span>{formatMessage({ id: 'paymentMethodNotAvailable' })}</span>}
          </MethodTypeDescription>
          {revaultRequired && <UnverifiedMethod onClick={onClickRevault} />}
        </div>
      </MethodTypeWrapper>
      {showDefault && (
        <DefaultIndicatorContainer
          onClick={onClickMethod}
          style={{ marginRight: `calc(${removable ? '0' : '1.6125rem'} + 1rem)`, marginTop: '8px' }}
        >
          <RadioButton
            label={''}
            name={dlv(method, 'credit.alias', dlv(method, 'prepaid.alias'))}
            value={method.accountIdentifier ?? method.fdAccountId ?? ''}
            checked={!!(showDefault && selected)}
          />
        </DefaultIndicatorContainer>
      )}
      {!hideDeleteIcon && removable && (
        <RemoveMethodWrapper>
          <RemoveMethod
            onClick={openPaymentDeletionConfirmation}
            data-testid="remove-card-button"
            aria-label={formatMessage({ id: 'removePaymentMethod' })}
            type="button"
          >
            <Tooltip text={formatMessage({ id: 'delete' })}>
              <IconDeleteCredit />
            </Tooltip>
          </RemoveMethod>
        </RemoveMethodWrapper>
      )}
      <DeletionConfirmationDialog
        data-testid="delete-confirmation-dialog"
        heading={formatMessage({ id: 'deletePaymentMethod' })}
        body={formatMessage({ id: 'confirmDeletePaymentMethod' })}
        modalAppearanceEventMessage="Confirmation: Delete Payment Method"
      />
      <ScanAndPayCardDeletionDialog data-testid="scan-and-pay-delete-confirmation-dialog" />
    </MethodContainer>
  );
}

export default PaymentMethodOption;
